/**
 * AI Agent Manager Page
 *
 * Purpose: Admin interface for managing AI Agent Definitions. Wraps
 *          SkeletonPage with content header + tenant selector + manager grid.
 * Architecture: Single-list page (no tabs). Mirrors WorkflowManagerPage's
 *              context loading + tenant switching pattern.
 * Author: Development Team
 * Created: 2026-05-23
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Home as HomeIcon,
  Public as GlobalIcon,
  SmartToyOutlined as AgentIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import SkeletonPage from '../../components/layout/SkeletonPage';
import AIAgentManager from '../../components/aiAgent/AIAgentManager';
import TenantSelector from '../../components/common/TenantSelector';
import { DismissibleBanner } from '../../components/common/DismissibleBanner';
import { useAuth } from '../../context/AuthContext';
import { metadataService } from '../../services/metadataService';

const ContentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

interface TenantInfo {
  tenant_id: string;
  name: string;
  is_global: boolean;
  can_edit: boolean;
}

interface MetadataManagerContext {
  current_tenant: TenantInfo;
  available_tenants: TenantInfo[];
  user_permissions: {
    can_edit_global: boolean;
    can_edit_tenant: boolean;
    can_switch_context: boolean;
  };
}

const AIAgentManagerPage: React.FC = () => {
  const { state } = useAuth();
  const { user } = state;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [context, setContext] = useState<MetadataManagerContext | null>(null);
  const [switchingContext, setSwitchingContext] = useState(false);

  const breadcrumbs = [
    { label: 'Home', href: '/home', icon: <HomeIcon fontSize="inherit" /> },
    { label: 'Admin', href: '/admin', icon: <AdminIcon fontSize="inherit" /> },
    { label: 'AI Agent Manager', icon: <AgentIcon fontSize="inherit" /> },
  ];

  const loadManagerContext = useCallback(
    async (explicitTenantId?: string, signal?: AbortSignal) => {
      try {
        setLoading(true);
        setError(null);
        if (signal?.aborted) return;

        const response = await metadataService.getManagerContext(explicitTenantId);
        if (!signal?.aborted) {
          if (response.data) {
            setContext(response.data);
          } else {
            throw new Error('No context data received from API');
          }
        }
      } catch (err) {
        if (!signal?.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load AI Agent Manager context');
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const abortController = new AbortController();
    loadManagerContext(user?.activeTenant?.tenant_id, abortController.signal);
    return () => abortController.abort();
  }, [loadManagerContext, user?.activeTenant?.tenant_id]);

  const handleTenantSwitch = async (newTenantId: string) => {
    if (!context || newTenantId === context.current_tenant.tenant_id) return;

    try {
      setSwitchingContext(true);
      setError(null);
      await metadataService.switchContext(newTenantId);
      const newTenant = context.available_tenants.find((t) => t.tenant_id === newTenantId);
      if (newTenant) {
        setContext({
          ...context,
          current_tenant: {
            tenant_id: newTenant.tenant_id,
            name: newTenant.name,
            is_global: newTenant.is_global,
            can_edit: newTenant.can_edit,
          },
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch tenant context');
    } finally {
      setSwitchingContext(false);
    }
  };

  if (loading) {
    return (
      <SkeletonPage pageTitle="AI Agent Manager" breadcrumbs={breadcrumbs}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </SkeletonPage>
    );
  }

  if (error || !context) {
    return (
      <SkeletonPage pageTitle="AI Agent Manager" breadcrumbs={breadcrumbs}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Failed to load AI Agent Manager'}
        </Alert>
      </SkeletonPage>
    );
  }

  const { current_tenant, available_tenants, user_permissions } = context;
  const canEdit = current_tenant.is_global
    ? user_permissions.can_edit_global
    : user_permissions.can_edit_tenant;

  return (
    <SkeletonPage pageTitle="AI Agent Manager" breadcrumbs={breadcrumbs}>
      <ContentHeader>
        <Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 0 }}>
            Manage AI Agent Definitions (skills, tools, system prompts)
          </Typography>
        </Box>
        {user_permissions.can_switch_context && (
          <TenantSelector
            currentTenant={current_tenant.tenant_id}
            availableTenants={available_tenants}
            onTenantChange={handleTenantSwitch}
            disabled={switchingContext}
            loading={switchingContext}
            size="small"
            dataTestId="tenant-context-selector"
          />
        )}
      </ContentHeader>

      {current_tenant.is_global && (
        <DismissibleBanner severity="warning" icon={<GlobalIcon />} useGlobalStyle>
          <Typography variant="subtitle1" component="div">
            You are managing GLOBAL AI Agents
          </Typography>
          <Typography variant="body2">
            Changes to GLOBAL AI Agents affect every tenant. Please use caution when making
            modifications.
          </Typography>
        </DismissibleBanner>
      )}

      <AIAgentManager tenantContext={current_tenant} canEdit={canEdit} />
    </SkeletonPage>
  );
};

export default AIAgentManagerPage;
